import type { Api } from "../../../data/api";

/** Hilfe & Support (0176): Tickets der Agentur, eigene Fehlermeldungen, Anhänge im Bucket „support“ unter t/<agentur>/… */

export type TicketStatus = "offen" | "in_bearbeitung" | "wartet_auf_kunde" | "geloest" | "geschlossen";
/** 0400: „einrichtung“ legt nur der Betrieb an (Antwort mit Creator-Liste auf die Testkonto-Mail), Kunden wählen sie nicht */
export type TicketKategorie = "fehler" | "frage" | "abrechnung" | "wunsch" | "einrichtung";
export interface Anhang { path: string; name: string; size?: number | null; type?: string | null }
export interface Ticket {
  id: string; number: number; org_id: string; created_by: string | null; category: TicketKategorie; subject: string;
  status: TicketStatus; priority: string; error_report_id: string | null; created_at: string; updated_at: string;
  last_customer_at: string | null; last_support_at: string | null;
}
export interface TicketNachricht { id: string; ticket_id: string; author_id: string | null; side: "kunde" | "support"; body: string; internal: boolean; attachments: Anhang[]; created_at: string }
export interface TicketEreignis { id: string; ticket_id: string; at: string; kind: string; from_value: string | null; to_value: string | null }
export interface EigeneMeldung { id: string; message: string; status: string; resolved_release: string | null; ticket_id: string | null; created_at: string; page: string | null }

const TICKET_COLS = "id, number, org_id, created_by, category, subject, status, priority, error_report_id, created_at, updated_at, last_customer_at, last_support_at";

export async function loadTickets(api: Pick<Api, "select">, orgId: string): Promise<Ticket[]> {
  return api.select<Ticket>("support_tickets", { columns: TICKET_COLS, filters: [["org_id", "eq", orgId]], order: [["updated_at", "desc"]], limit: 200 });
}
export async function loadTicket(api: Pick<Api, "select">, id: string): Promise<{ ticket: Ticket | null; messages: TicketNachricht[]; events: TicketEreignis[] }> {
  const [t, m, e] = await Promise.all([
    api.select<Ticket>("support_tickets", { columns: TICKET_COLS, filters: [["id", "eq", id]] }),
    api.select<TicketNachricht>("support_ticket_messages", { filters: [["ticket_id", "eq", id]], order: [["created_at", "asc"]] }),
    api.select<TicketEreignis>("support_ticket_events", { filters: [["ticket_id", "eq", id]], order: [["at", "asc"]] }),
  ]);
  return { ticket: t[0] ?? null, messages: m, events: e };
}
export async function loadEigeneMeldungen(api: Pick<Api, "select">, userId: string): Promise<EigeneMeldung[]> {
  return api.select<EigeneMeldung>("error_reports", {
    columns: "id, message, status, resolved_release, ticket_id, created_at, page", filters: [["user_id", "eq", userId]], order: [["created_at", "desc"]], limit: 50,
  });
}
export async function loadMeldung(api: Pick<Api, "select">, id: string): Promise<EigeneMeldung | null> {
  return (await api.select<EigeneMeldung>("error_reports", { columns: "id, message, status, resolved_release, ticket_id, created_at, page", filters: [["id", "eq", id]] }))[0] ?? null;
}

/** Dateiname für den Speicher: nur sichere Zeichen, Länge begrenzt */
export function sichererName(name: string): string {
  const n = name.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w.-]+/g, "_").replace(/_+/g, "_").replace(/^[._]+/, "");
  return (n || "datei").slice(-80);
}
export const MAX_ANHANG_BYTES = 10 * 1024 * 1024;
/** Dateiarten, die der Bucket „support“ annimmt (0179, gleiche Liste); sonst lehnt der Speicher erst nach dem Hochladen ab */
export const ANHANG_TYPEN = [
  "image/png", "image/jpeg", "image/webp", "image/gif", "image/heic", "image/heif",
  "application/pdf", "text/plain", "text/csv",
  "video/mp4", "video/quicktime", "video/webm",
] as const;
export function anhangTypErlaubt(file: Pick<File, "type">): boolean {
  return (ANHANG_TYPEN as readonly string[]).includes((file.type || "").toLowerCase());
}
export async function anhangHochladen(api: Pick<Api, "upload">, orgId: string, file: File): Promise<Anhang> {
  if (file.size > MAX_ANHANG_BYTES) throw new Error("zu_gross");
  if (!anhangTypErlaubt(file)) throw new Error("typ");
  const id = crypto.randomUUID();
  const path = `t/${orgId}/${id}-${sichererName(file.name)}`;
  await api.upload("support", path, file);
  return { path, name: file.name.slice(0, 160), size: file.size, type: file.type || null };
}

export async function ticketAnlegen(api: Pick<Api, "rpc">, orgId: string, b: { category: TicketKategorie; subject: string; body: string; attachments: Anhang[]; errorReport?: string | null }): Promise<string> {
  return api.rpc<string>("support_ticket_create", {
    p_org: orgId, p_category: b.category, p_subject: b.subject, p_body: b.body, p_attachments: b.attachments, p_error_report: b.errorReport ?? null,
  });
}
export async function ticketAntworten(api: Pick<Api, "rpc">, id: string, body: string, attachments: Anhang[]): Promise<void> {
  await api.rpc("support_ticket_reply", { p_ticket: id, p_body: body, p_attachments: attachments });
}
export async function ticketLoesen(api: Pick<Api, "rpc">, id: string): Promise<void> {
  await api.rpc("support_ticket_resolve", { p_ticket: id });
}
